import { getSiteOrigin } from './constants';
import { getProductUrl } from './urls';

const stripHtml = (value = '') => String(value).replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();

const normalizeSocialUrl = (value) => {
  const trimmed = String(value || '').trim();
  if (!trimmed) return '';
  if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
  return `https://${trimmed.replace(/^@/, '')}`;
};

const parseUkraineAddress = (raw = '') => {
  const text = String(raw).trim();
  if (!text) return null;

  const parts = text.split(',').map((part) => part.trim()).filter(Boolean);
  const region = parts.find((part) => /область/i.test(part));
  const country = parts.find((part) => /^україн/i.test(part)) ? 'UA' : 'UA';
  const locality = parts.find(
    (part) => part !== region && !/^україн/i.test(part) && !/область/i.test(part)
  ) || parts[0];

  return {
    '@type': 'PostalAddress',
    streetAddress: parts[0] || text,
    addressLocality: locality || undefined,
    addressRegion: region || undefined,
    addressCountry: country,
  };
};

export const upsertJsonLd = (id, payload) => {
  if (!payload) {
    document.getElementById(id)?.remove();
    return;
  }

  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.type = 'application/ld+json';
    el.id = id;
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(payload);
};

export const removeJsonLd = (id) => {
  document.getElementById(id)?.remove();
};

export const buildBreadcrumbJsonLd = (items = []) => {
  if (!items.length) return null;
  const origin = getSiteOrigin();

  return {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: items.map((item, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: item.label,
      ...(item.path ? { item: `${origin}${item.path}` } : {}),
    })),
  };
};

export const buildCategoryItemListJsonLd = (products = []) => {
  if (!products.length) return null;

  const origin = getSiteOrigin();

  return {
    '@context': 'https://schema.org',
    '@type': 'ItemList',
    itemListElement: products.slice(0, 20).map((product, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      url: `${origin}${getProductUrl(product)}`,
      name: product.title,
    })),
  };
};

export const buildProductJsonLd = (product) => {
  if (!product?.id) return null;

  const origin = getSiteOrigin();
  const url = `${origin}${getProductUrl(product)}`;
  const description = stripHtml(product.metaDescription || product.shortDescription || product.description || product.title);
  const imagePath = product.images?.[0];
  const image = imagePath
    ? (imagePath.startsWith('http') ? imagePath : `${origin}${imagePath.startsWith('/') ? imagePath : `/${imagePath}`}`)
    : `${origin}/static/media/og-preview.jpg`;

  return {
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: product.title,
    description,
    image: [image],
    url,
    sku: product.article || String(product.id),
    ...(product.category?.name ? { category: product.category.name } : {}),
    offers: {
      '@type': 'Offer',
      priceCurrency: 'UAH',
      price: String(product.price),
      availability: product.inStock === false
        ? 'https://schema.org/OutOfStock'
        : 'https://schema.org/InStock',
      url,
    },
  };
};

export const buildOrganizationJsonLd = (contacts) => {
  if (!contacts) return null;

  const origin = getSiteOrigin();
  const phones = (contacts.phones || []).map((phone) => phone.replace(/[^\d+]/g, '')).filter(Boolean);
  const sameAs = [
    contacts.instagram,
    contacts.facebook,
    contacts.youtube,
  ].map(normalizeSocialUrl).filter(Boolean);

  const contactPoint = phones.map((phone) => ({
    '@type': 'ContactPoint',
    telephone: phone,
    contactType: 'customer service',
    areaServed: 'UA',
    availableLanguage: ['Ukrainian', 'Russian'],
  }));

  return {
    '@context': 'https://schema.org',
    '@type': 'AutoPartsStore',
    name: contacts.company || 'ARKDetali',
    alternateName: 'ARKDetali',
    url: origin || undefined,
    logo: origin ? `${origin}/favicon.ico` : undefined,
    image: origin ? `${origin}/static/media/og-preview.jpg` : undefined,
    email: contacts.email || undefined,
    telephone: phones[0] || undefined,
    priceRange: '₴₴',
    currenciesAccepted: 'UAH',
    paymentAccepted: 'Cash, Credit Card',
    areaServed: {
      '@type': 'Country',
      name: 'Ukraine',
    },
    address: parseUkraineAddress(contacts.address),
    contactPoint: contactPoint.length ? contactPoint : undefined,
    sameAs: sameAs.length ? sameAs : undefined,
  };
};

export const buildReviewsJsonLd = (reviews = []) => {
  const rated = reviews.filter((review) => review?.rating > 0);
  if (!rated.length) return null;

  const origin = getSiteOrigin();
  const aggregateRating = {
    '@type': 'AggregateRating',
    ratingValue: (rated.reduce((sum, item) => sum + Number(item.rating), 0) / rated.length).toFixed(1),
    reviewCount: String(rated.length),
    bestRating: '5',
    worstRating: '1',
  };

  return {
    '@context': 'https://schema.org',
    '@type': 'Organization',
    name: 'ARKDetali',
    url: origin || undefined,
    aggregateRating,
    review: rated.slice(0, 10).map((review) => ({
      '@type': 'Review',
      author: {
        '@type': 'Person',
        name: review.User?.name || 'Покупець',
      },
      datePublished: review.createdAt || undefined,
      reviewBody: review.text,
      reviewRating: {
        '@type': 'Rating',
        ratingValue: String(review.rating),
        bestRating: '5',
        worstRating: '1',
      },
      ...(review.productId
        ? { itemReviewed: { '@type': 'Product', url: `${origin}${getProductUrl({ id: review.productId, slug: review.productSlug })}` } }
        : {}),
    })),
  };
};

export const buildWebSiteJsonLd = () => {
  const origin = getSiteOrigin();
  if (!origin) return null;

  return {
    '@context': 'https://schema.org',
    '@type': 'WebSite',
    name: 'ARKDetali',
    url: origin,
    inLanguage: 'uk-UA',
    potentialAction: {
      '@type': 'SearchAction',
      target: `${origin}/search?q={search_term_string}`,
      'query-input': 'required name=search_term_string',
    },
  };
};
